import clsx from "classnames";
import type { ReactNode } from "react";
import React from "react";
import type { ButtonProps as BsButtonProps } from "react-bootstrap";
import { Button as BsButton } from "react-bootstrap";

export interface ButtonProps extends BsButtonProps {
	/**
	 * Whether the button should trigger a download
	 *
	 * @default false
	 */
	download?: boolean;

	/**
	 * Icon component to display
	 */
	icon?: ReactNode;

	/**
	 * Use outlined style instead of filled background
	 *
	 * @default false
	 */
	outlined?: boolean;

	/**
	 * Make button circular/rounded
	 *
	 * @default false
	 */
	rounded?: boolean;

	/**
	 * Relationship attribute for links
	 */
	rel?: string;

	/**
	 * Position of icon relative to text
	 *
	 * @default "start"
	 */
	iconPosition?: "start" | "end";
}

/**
 * Button components are used to trigger actions in forms, dialogs, and more.
 */
export const Button = React.forwardRef<HTMLElement, ButtonProps>(
	({ className, variant = "primary", icon, children, rounded, outlined, iconPosition = "start", ...props }, ref) => {
		const iconOnly = !children && !!icon;
		const classes = clsx(
			"btn-ui3",
			className,
			iconOnly && "btn-icon-only",
			iconOnly && (props.size === "sm" ? "px-2" : "px-3"),
			rounded && "rounded-circle",
		);
		const variantAlias = outlined ? `outline-${variant}` : variant;

		return (
			<BsButton {...props} ref={ref} className={classes} variant={variantAlias}>
				{icon && iconPosition === "start" && (
					<span className={`btn-fa-icon${iconOnly ? "" : " mr-2"}`}>{icon}</span>
				)}
				{children}
				{icon && iconPosition === "end" && (
					<span className={`btn-fa-icon${iconOnly ? "" : " ml-2"}`}>{icon}</span>
				)}
			</BsButton>
		);
	},
);
